import { useEffect } from "react";
import "./PreorderInfoModal.css";

/* ── Icons ── */

const BoltIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M13.5 2 4 13.5h6L9.5 22 19 10.5h-6L13.5 2Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const LeafIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M19.5 4.5c0 8-4.6 12.4-10.4 12.4-1.4 0-2.6-.3-3.6-.8C6.7 9.4 11.6 5.3 19.5 4.5Z"
      fill="currentColor"
    />
    <path
      d="M5 20c1.2-4.6 4-8 8.5-10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const CrossMark = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M7.5 7.5 16.5 16.5M16.5 7.5 7.5 16.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
  </svg>
);

const CheckMark = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M6.5 12.5 10.5 16.5 17.5 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Faint line-art watermarks ── */

const ClocheWatermark = () => (
  <svg
    className="pim-watermark"
    viewBox="0 0 120 96"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <path d="M30 20q30-16 60 0" opacity="0.55" />
      <path d="M38 32q22-11 44 0" opacity="0.55" />
      <path d="M18 78a42 42 0 0 1 84 0" />
      <path d="M10 78h100" />
      <circle cx="60" cy="40" r="4" />
    </g>
  </svg>
);

const PotWatermark = () => (
  <svg
    className="pim-watermark"
    viewBox="0 0 120 96"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <path d="M22 34h76" />
      <path d="M28 34l5 46h54l5-46" />
      <path d="M22 44H12a6 6 0 0 0 0 12h11" />
      <path d="M98 44h10a6 6 0 0 1 0 12H97" />
      <path d="M52 20h16" />
      <path d="M60 14v6" />
      <path d="M68 52c0 7-5 12-12 12" />
      <path d="M68 52c-9 0-14 5-14 12" />
    </g>
  </svg>
);

/* ── Content ── */

const RESTAURANT_POINTS = [
  "Most dishes are ready before you order.",
  "Often relies on pre-made gravies or reheated food.",
  "May include food colours, reused cooking oil, and flavour enhancers.",
];

const DAILYDISH_POINTS = [
  "Nothing is pre-prepared.",
  "Cooked from scratch using fresh ingredients.",
  "Fresh cooking oil. No food colours or flavour enhancers—just like home.",
];

const PreorderInfoModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="pim-overlay" role="dialog" aria-modal="true" aria-label="Why DailyDish cooks fresh">
      <div className="pim-backdrop" onClick={onClose} />

      <div className="pim-sheet">
        <div className="pim-scroll">
          <div className="pim-header">
            <button className="pim-close-btn" onClick={onClose} aria-label="Close">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                  d="M7 7l10 10M17 7L7 17"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <h2 className="pim-title">
            Home Food
            <br />
            without the cooking
          </h2>
          {/* ── Restaurant delivery apps ── */}
          <section className="pim-card pim-card--warm">
            <ClocheWatermark />

            <div className="pim-card-head">
              <span className="pim-card-icon pim-card-icon--warm">
                <BoltIcon />
              </span>
              <h3 className="pim-card-title pim-card-title--warm">Quick Food Delivery</h3>
            </div>

            <p className="pim-card-tagline pim-card-tagline--warm">Food is already waiting.</p>

            <hr className="pim-divider pim-divider--warm" />

            <ul className="pim-points">
              {RESTAURANT_POINTS.map((point) => (
                <li className="pim-point" key={point}>
                  <span className="pim-point-mark pim-point-mark--warm">
                    <CrossMark />
                  </span>
                  <span className="pim-point-text">{point}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ── DailyDish ── */}
          <section className="pim-card pim-card--green">
            <PotWatermark />

            <div className="pim-card-head">
              <span className="pim-card-icon pim-card-icon--green">
                <LeafIcon />
              </span>
              <h3 className="pim-card-title pim-card-title--green">DailyDish</h3>
            </div>

            <p className="pim-card-tagline pim-card-tagline--green">
              Everything begins after you order.
            </p>

            <hr className="pim-divider pim-divider--green" />

            <ul className="pim-points">
              {DAILYDISH_POINTS.map((point) => (
                <li className="pim-point" key={point}>
                  <span className="pim-point-mark pim-point-mark--green">
                    <CheckMark />
                  </span>
                  <span className="pim-point-text">{point}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ── Closing strip ── */}
          <div className="pim-strip">
            <span className="pim-strip-icon">
              <LeafIcon />
            </span>
            <p className="pim-strip-text">DailyDish is optimised for home quality food.</p>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="pim-footer">
          <button className="pim-cta-btn" onClick={onClose}>
            Yes, cook it fresh for me
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreorderInfoModal;
